import { useState, useCallback } from 'react';
import { DEFAULT_MIRROR_ID, fallbackMirror, type MirrorInfo } from '../config/mirrors';
import type { VacancyId } from '../config/vacancies';
import { clearAllPortalStorage, migrateLegacyStorage } from '../lib/storage';
// Взаимный импорт с themes.ts безопасен: обе стороны вызывают друг друга только внутри функций.
import { rememberTheme } from '../config/themes';

const AUTH_API = '/api/portal/auth';

export interface AuthUser {
  username: string;
  name: string;
  team_id: string;
  mirror: string;
}

export interface ManagerInfo {
  name: string;
  telegram: string;
  email: string;
  avatar: string | null;
}

const AUTH_KEY = 'portal_auth_user';
const MANAGER_KEY = 'portal_manager';
const MIRROR_KEY = 'portal_mirror';

export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthUser;
    // Старые сессии (до зеркал) поля mirror не имеют — это зеркало default.
    return { ...parsed, mirror: parsed.mirror || DEFAULT_MIRROR_ID };
  } catch {
    return null;
  }
}

export function getStoredManager(): ManagerInfo | null {
  try {
    const raw = localStorage.getItem(MANAGER_KEY);
    return raw ? (JSON.parse(raw) as ManagerInfo) : null;
  } catch {
    return null;
  }
}

/** Никогда не возвращает null — только валидное зеркало или дефолт. */
export function getStoredMirror(): MirrorInfo {
  try {
    const raw = localStorage.getItem(MIRROR_KEY);
    if (!raw) return fallbackMirror(getStoredUser()?.mirror ?? DEFAULT_MIRROR_ID);
    const parsed = JSON.parse(raw) as MirrorInfo;
    if (!parsed?.id || !Array.isArray(parsed.vacancies) || parsed.vacancies.length === 0) {
      return fallbackMirror(parsed?.id ?? DEFAULT_MIRROR_ID);
    }
    return parsed;
  } catch {
    return fallbackMirror(DEFAULT_MIRROR_ID);
  }
}

/**
 * Единственная точка выхода. Чистит сессию И всё портальное состояние.
 * Перед очисткой запоминает тему зеркала для экрана входа — так её получают и сессии,
 * открытые до появления portal_last_theme (без повторного входа ключа у них нет).
 */
export function performLogout(): void {
  if (getStoredUser()) rememberTheme(getStoredMirror().id);
  clearAllPortalStorage();
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);

  const login = useCallback(async (loginVal: string, password: string): Promise<boolean> => {
    const res = await fetch(AUTH_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: loginVal.trim(), password: password.trim() }),
    });

    if (!res.ok) return false;
    const data = await res.json();
    if (!data?.ok || !data.user) return false;

    const mirrorId: string = data.user.mirror || DEFAULT_MIRROR_ID;

    const authUser: AuthUser = {
      username: data.user.username,
      name: data.user.full_name,
      team_id: data.user.team_id,
      mirror: mirrorId,
    };
    localStorage.setItem(AUTH_KEY, JSON.stringify(authUser));

    // Зеркало: из ответа бэка, иначе фолбэк (старый бэк / откат).
    const fromApi = data.mirror;
    const mirrorInfo: MirrorInfo =
      fromApi?.id && Array.isArray(fromApi.vacancies) && fromApi.vacancies.length
        ? { id: fromApi.id, label: fromApi.label ?? fromApi.id, vacancies: fromApi.vacancies as VacancyId[] }
        : fallbackMirror(mirrorId);
    localStorage.setItem(MIRROR_KEY, JSON.stringify(mirrorInfo));

    const m = data.user.manager;
    if (m) {
      const manager: ManagerInfo = {
        name: m.name, telegram: m.telegram, email: m.email, avatar: m.avatar ?? null,
      };
      localStorage.setItem(MANAGER_KEY, JSON.stringify(manager));
    } else {
      localStorage.removeItem(MANAGER_KEY);
    }

    if (mirrorId === DEFAULT_MIRROR_ID) migrateLegacyStorage(authUser.username);

    setUser(authUser);
    return true;
  }, []);

  const logout = useCallback(() => {
    performLogout();
    setUser(null);
  }, []);

  return { user, login, logout };
}
